import React, { useState, useEffect, useRef } from 'react'
import { AdyenCheckout, ApplePay } from '@adyen/adyen-web'
import { useAccessToken, usePaymentMethodsForBasket, useShippingMethodsForShipment, useShopperBasketsMutation, useShopperOrdersMutation, ShopperOrdersMutations } from '@salesforce/commerce-sdk-react'
import { useCurrentBasket } from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import { getSiteEnvVarValue, getBrandSiteEnvVarValue, getBrandLocaleEnvVarValue } from '../../../utils/site-config'
import { useIntl } from 'react-intl'
import useBasketBrand from '../../../hooks/use-basket-brand'
import { sendClickEvent, sendBeginCheckoutEvent } from '../../../utils/gtm-utils'
//fetch SFCC payment methods and filter out & return the Apple Pay payment method
const useApplePayPaymentMethod = (basket) => {
    const { data: paymentMethods } = usePaymentMethodsForBasket({
        parameters: {
            basketId: basket?.basketId
        }
    })
    const adyenPaymentInstrument = paymentMethods?.applicablePaymentMethods?.find(
        ({ id }) => id === 'AdyenComponent'
    )
    const adyenPaymentMethods = adyenPaymentInstrument?.c_adyenPaymentMethods
    const applePayPaymentMethod = adyenPaymentMethods?.paymentMethods?.find(
        ({ type }) => type === 'applepay'
    )
    return applePayPaymentMethod
}

const getEstimatedBasketTotal = (basket) => {
    const discountedMerchandiseTotal = Number(basket?.productTotal ?? basket?.productSubTotal) || 0
    const estimatedShipping = Number(basket?.shippingTotal) || 0
    const estimatedTax = basket?.taxTotal != null ? Number(basket.taxTotal) : 0
    const estimatedTotalValue =
        basket?.orderTotal > 0
            ? Number(basket.orderTotal)
            : (Number(basket?.productTotal) || discountedMerchandiseTotal) + estimatedShipping + estimatedTax
    return estimatedTotalValue
}

const getApplePayShippingMethodAmount = (shippingMethod, basket) => {
    const selectedShippingMethodId = basket?.shipments?.[0]?.shippingMethod?.id
    const selectedShipmentTotal = basket?.shipments?.[0]?.shippingTotal
    const adjustedShippingTotal = basket?.shippingTotal ?? selectedShipmentTotal
    const isSelectedMethod = shippingMethod?.id === selectedShippingMethodId

    if (isSelectedMethod && Number(adjustedShippingTotal) === 0) {
        return '0.00'
    }

    return (Number(shippingMethod?.price) || 0).toFixed(2)
}

const getApplePayContactField = (data, field) =>
    data?.authorizedEvent?.payment?.shippingContact?.[field] ||
    data?.authorizedEvent?.payment?.billingContact?.[field]

const ApplePayExpressButton = ({ setErrorMessage }) => {
    const intl = useIntl()
    const [isApplePayAvailable, setIsApplePayAvailable] = useState(false)
    const applePayContainerRef = useRef(null) // the container div for the Apple Pay button
    const applePayComponentRef = useRef(null)
    const createOrderResponseRef = useRef(null) // to save the new order created in SFCC after Apple Pay authorization in onSubmit

    const { data: basket, refetch: refetchCurrentBasket } = useCurrentBasket()
    const syncBasketBrand = useBasketBrand()
    const { data: customer } = useCurrentCustomer()
    const { isRegistered } = customer || {}
    // fetch the Apple Pay payment method for the basket from applicable payment methods in SFCC
    const applePayPaymentMethod = useApplePayPaymentMethod(basket)

    // fetch the shipping methods for the shipment in the basket from SFCC
    const shipmentId = basket?.shipments?.[0]?.shipmentId
    const { data: shippingMethods } = useShippingMethodsForShipment({
        parameters: {
            basketId: basket?.basketId,
            shipmentId
        }
    })

    const { app: appConfig } = getConfig() //get ssr config

    const { locale, site } = useMultiSite()

        
    const CONTACT_US_TEL = getBrandLocaleEnvVarValue(appConfig, 'CONTACT_US_TEL', site?.id, locale?.id)
    const showGenericPaymentError = () => {
        setErrorMessage(
            intl.formatMessage({
                id: 'cart.message.generic_banner_error',
                defaultMessage:
                    'There’s a problem with your payment. Please try again or try a different payment method. If the problem persists, contact our customer service at {phoneNumber}.'
            }, { phoneNumber: CONTACT_US_TEL })
        )
    }

    const isUserCancelledApplePay = (errorOrResult) => {
        const name = errorOrResult?.name || ''
        const message = errorOrResult?.message || ''

        return (
            name.includes('CANCEL') ||
            message.includes('cancel') ||
            message.includes('Window is closed')
        )
    }
    // mutation to update basket shipping method when user selects a shipping method in Apple Pay modal
    const { mutateAsync: updateShippingMethodForShipment } = useShopperBasketsMutation('updateShippingMethodForShipment')
    const { mutateAsync: updateShippingAddressForShipment } = useShopperBasketsMutation('updateShippingAddressForShipment')
    const { mutateAsync: updateBillingAddressForBasket } = useShopperBasketsMutation('updateBillingAddressForBasket')
    const { mutateAsync: addPaymentInstrumentToBasket } = useShopperBasketsMutation('addPaymentInstrumentToBasket')
    const { mutateAsync: removePaymentInstrumentFromBasket } = useShopperBasketsMutation('removePaymentInstrumentFromBasket')
    const { mutateAsync: updateCustomerForBasket } = useShopperBasketsMutation('updateCustomerForBasket')
    const { mutateAsync: updateBasket } = useShopperBasketsMutation('updateBasket')
    
    const applePayValidationData = applePayPaymentMethod?.configuration // Apple merchant config values in Adyen
    const applePayDomain = getSiteEnvVarValue(appConfig, 'ADYEN_APPLEPAY_DOMAIN', site?.id)
    const applePayDomainBrand = getBrandSiteEnvVarValue(appConfig, applePayDomain, site?.id)
    const { getTokenWhenReady } = useAccessToken()
    const basketId = basket?.basketId
    const basketCurrency = basket?.currency
    const basketAmountValue = Math.round(getEstimatedBasketTotal(basket) * 100)
    const localeId = locale?.id
    const siteId = site?.id
    const shippingMethodCount = shippingMethods?.applicableShippingMethods?.length || 0
    const applePayPaymentMethodType = applePayPaymentMethod?.type
    const applePayMerchantName = applePayPaymentMethod?.configuration?.merchantName
    
    // update the SFCC order's Payment Instrument with the result recieved from Adyen authorization in onSubmit
    const updatePaymentInstrument = useShopperOrdersMutation(ShopperOrdersMutations.UpdatePaymentInstrumentForOrder)
    const updateOrderPaymentInstrument = async (adyenPaymentResult) => {
        const createOrderResponse = createOrderResponseRef.current

        const adyenInstrument = createOrderResponse.paymentInstruments.find(
            (paymentInstrument) => paymentInstrument.paymentMethodId === 'AdyenComponent')

        const updatedOrderWithPaymentIntrument = await updatePaymentInstrument.mutateAsync({
            parameters: {
                orderNo: createOrderResponse.orderNo,
                paymentInstrumentId: adyenInstrument.paymentInstrumentId
            },
            body: {
                amount: adyenPaymentResult?.amount?.value
                    ? adyenPaymentResult.amount.value / 100
                    : undefined,
                paymentMethodId: 'AdyenComponent',
                c_adyenPaymentData: JSON.stringify(adyenPaymentResult)
            }
        })

    }


    // set fingerPrint required for Adyen authorization of applepay onSubmit
    const [fingerprintScriptLoaded, setFingerprintScriptLoaded] = useState(false)
    useEffect(() => {
        if (!document.getElementById('adyen-device-fingerprint')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.id = 'adyen-device-fingerprint'
            input.name = 'adyen-device-fingerprint'
            document.body.appendChild(input)
        }

        if (!fingerprintScriptLoaded) {
            const script = document.createElement('script')

            const now = new Date()
            const yyyy = now.getFullYear()
            const mm = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const version = `${yyyy}${mm}${dd}`

            script.src = `https://live.adyen.com/hpp/js/df.js?v=${version}`
            script.async = true
            script.nonce = window.__CSP_NONCE__

            script.onload = () => {
                setFingerprintScriptLoaded(true)
                window.dfSet('adyen-device-fingerprint')
            }

            document.body.appendChild(script)
        }
    }, [fingerprintScriptLoaded])

    // initialize the Adyen Checkout instance and mount the Apple Pay component in the container div
    useEffect(() => {
        const initAdyenCheckoutAndLoadApplePayInContainer = async () => {
            const countryCode = locale.id.split('-')[1]

            const adyenAmountObj = {
                value: Math.round(getEstimatedBasketTotal(basket) * 100),
                currency: basket.currency
            }
            const adyenCheckoutObj = await AdyenCheckout({
                clientKey: getSiteEnvVarValue(appConfig, 'ADYEN_CLIENT_KEY', site?.id),
                environment: getSiteEnvVarValue(appConfig, 'ADYEN_MODE', site?.id),
                locale: locale.id,
                countryCode,
                amount: adyenAmountObj,
                paymentMethodsResponse: {
                    paymentMethods: [applePayPaymentMethod]
                },
                onSubmit: async (state, component, actions) => {

                    // Keep the loyalty points instrument paired with any applied points coupon.
                    const paymentInstrumentsToRemove = (basket.paymentInstruments || []).filter(
                        (paymentInstrument) => paymentInstrument.paymentMethodId !== 'LOYALTY_POINTS'
                    )
                    for (const paymentInstrument of paymentInstrumentsToRemove) {
                        await removePaymentInstrumentFromBasket({
                            parameters: {
                                basketId: basket.basketId,
                                paymentInstrumentId: paymentInstrument.paymentInstrumentId
                            }
                        })
                    }
                    const signifydParameters = {
                        paymentMethod: state.data.paymentMethod
                    }

                    const signifydJsonString = JSON.stringify(signifydParameters)

                    const updatedBasket = await addPaymentInstrumentToBasket({
                        parameters: {
                            basketId: basket.basketId
                        },
                        body: {
                            paymentMethodId: 'AdyenComponent',
                            amount: getEstimatedBasketTotal(basket),
                            c_signifydPaymentData: signifydJsonString
                        }
                    })

                    const token = await getTokenWhenReady()
                    
                    // add brand to the basket
                    await syncBasketBrand() 
                    
                    const updatedBasketWithExpressFlags = await updateBasket({
                        parameters: {basketId: basket.basketId},
                        body: {
                            c_isApplePayExpressOrder: true
                        }
                    })


                    const createOrderRaw = await fetch(
                        `${proxyPath}/checkout/shopper-orders/v1/organizations/${organizationId}/orders?siteId=${siteId}`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                basketId: basket.basketId
                            })
                        }
                    )

                    const createOrderResponse = await createOrderRaw.json()
                    // Check Signifyd
                    if (createOrderResponse.c_SignifydPolicy == 'REJECT' || createOrderResponse.status == 'failed') {
                        const paymentInstruments = (
                            updatedBasket?.paymentInstruments || basket.paymentInstruments || []
                        ).filter((paymentInstrument) => paymentInstrument.paymentMethodId !== 'LOYALTY_POINTS')

                        for (const paymentInstrument of paymentInstruments) {
                            await removePaymentInstrumentFromBasket({
                                parameters: {
                                    basketId: basket.basketId,
                                    paymentInstrumentId: paymentInstrument.paymentInstrumentId
                                }
                            })
                        }
                        showGenericPaymentError()
                        actions.reject()
                        component?.setStatus?.('ready')
                        return
                    }
                    createOrderResponseRef.current = createOrderResponse
                    const paymentResult = await fetch(
                        `${proxyPath}/custom/adyen-payments/v1/organizations/${organizationId}/adyen-payments?siteId=${siteId}&c_brand=Garage`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                orderId: createOrderResponse.orderNo,
                                orderToken: createOrderResponse.orderToken,
                                adyenData: state,
                                deviceFingerprint: document.getElementById('adyen-device-fingerprint').value,
                                locale,
                                returnUrl: `${window.location.origin}/adyen?orderNo=${createOrderResponse.orderNo}`
                            })
                        }
                    )

                    const adyenPaymentResult = await paymentResult.json()

                    await updateOrderPaymentInstrument(adyenPaymentResult)
                    
                    if (!adyenPaymentResult.resultCode || adyenPaymentResult.resultCode === 'Error') {
                        actions.reject() // will call onPaymentFailed
                        return
                    }

                    actions.resolve(adyenPaymentResult) // will call onPaymentCompleted
                },
                onPaymentCompleted: async (result, component) => {

                    const createOrderResponse = createOrderResponseRef.current

                    
                    if (result?.resultCode?.toLowerCase() === 'authorised') {
                        window.location.href = `/checkout/confirmation/${createOrderResponse?.orderNo || ''}`
                    }
                },

                onPaymentFailed: (result, component) => {
                    showGenericPaymentError()
                    component?.setStatus?.('ready')
                    applePayComponentRef.current?.unmount()
                    applePayComponentRef.current = null
                    initAdyenCheckoutAndLoadApplePayInContainer()
                },

                onError: (error, component) => {
                      if (!isUserCancelledApplePay(error)) {
                          showGenericPaymentError()
                      }
                    component?.setStatus?.('ready')
                    applePayComponentRef.current?.unmount()
                    applePayComponentRef.current = null
                    initAdyenCheckoutAndLoadApplePayInContainer()
                }
            })
            const parameters = {
                ...appConfig.commerceAPI.parameters
            }
            const proxyPath = appConfig.commerceAPI.proxyPath
            const organizationId = parameters.organizationId
            const siteId = site?.id
            //create ApplePay component/HTML and mount it to the container
            try {
                applePayComponentRef.current = new ApplePay(adyenCheckoutObj, {
                    isExpress: true,
                    expressPage: 'cart',
                    buttonColor: 'white-outline',
                    buttonType: 'plain',
                    amount: adyenAmountObj,
                    countryCode,
                    requiredBillingContactFields: ['postalAddress', 'name'],
                    requiredShippingContactFields: [
                        'postalAddress',
                        'name',
                        'phone',
                        'email'
                    ],
                    onValidateMerchant: async (resolve, reject, validationURL) => {
                        try {
                            //send gtm event
                            sendClickEvent('applepay_cta_cartpage')
                            sendBeginCheckoutEvent(basket, {
                                checkout_entry_method: 'apple_pay',
                                checkout_location: 'cart_page',
                                checkout_type: 'express',
                                payment_type: 'apple_pay'
                            })
                            const token = await getTokenWhenReady()

                            const response = await fetch(
                                `${proxyPath}/custom/applepay-validation/v1/organizations/${organizationId}/applepay-validation?siteId=${siteId}`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`
                                    },
                                    body: JSON.stringify({
                                        merchantIdentifier: applePayValidationData.merchantId,
                                        displayName: applePayValidationData.merchantName,
                                        domainName: applePayDomainBrand || applePayDomain,
                                        validationURL
                                    })
                                }
                            )

                            const responseJson = await response.json()
                            resolve(responseJson)
                        } catch (error) {
                            console.error('ApplePay validation error:', error)
                            reject(error)
                        }
                    },

                    onShippingContactSelected: async (resolve, reject, event) => {
                        try {
                            const ApplePayErrorCtor = window.ApplePayError
                            const shippingCountryCode =
                                event.shippingContact?.countryCode?.toUpperCase()

                            let update = {}
                            const postalCodePrefixesToRestrict = getSiteEnvVarValue(appConfig, 'POSTAL_CODE_PREFIXES_TO_RESTRICT', site?.id) //reject address if first 2 characters of postal code are in it
                            if (shippingCountryCode !== countryCode|| 
                                    postalCodePrefixesToRestrict?.split(',').includes(event.shippingContact?.postalCode.trim().toUpperCase().substring(0,2)) ) {
                                const shippingCountryError = ApplePayErrorCtor
                                    ? new ApplePayErrorCtor(
                                        'shippingContactInvalid',
                                        'countryCode',
                                        'Cannot ship to the selected address'
                                    )
                                    : {
                                        code: 'shippingContactInvalid',
                                        contactField: 'countryCode',
                                        message: 'Cannot ship to the selected address'
                                    }

                                update = {
                                    // Get the total from the application state.
                                    newTotal: {
                                        label: applePayValidationData.merchantName,
                                        amount: getEstimatedBasketTotal(basket).toFixed(2),
                                        type: 'final'
                                    },
                                    errors: [shippingCountryError]
                                }
                                resolve(update)
                                return
                            }
                            await updateShippingAddressForShipment({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: basket.shipments[0].shipmentId
                                },
                                body: {
                                    city: event?.shippingContact?.locality,
                                    stateCode: event?.shippingContact?.administrativeArea,
                                    postalCode: event?.shippingContact?.postalCode?.trim().toUpperCase(),
                                    countryCode: event?.shippingContact?.countryCode
                                }
                            })

                            const filterApplicableShippingMethods = shippingMethods?.applicableShippingMethods?.filter(
                                (shippingMethod) => shippingMethod.c_isAvailableForApplePayExpress
                            )
                            const applePayShippingMethods =
                                filterApplicableShippingMethods?.map(
                                    (shippingMethod) => ({
                                        identifier: shippingMethod.id,
                                        label: shippingMethod.name,
                                        detail: shippingMethod.c_estimatedArrivalTime || '',
                                        amount: getApplePayShippingMethodAmount(shippingMethod, basket)
                                    })
                                ) || []
                            update = {
                                newShippingMethods: applePayShippingMethods,
                                newTotal: {
                                    label: applePayValidationData.merchantName,
                                    amount: getEstimatedBasketTotal(basket).toFixed(2),
                                    type: 'final'
                                }
                            }
                            resolve(update)
                        } catch (error) {
                            console.error('ApplePay onShippingContactSelected error:', error)
                            reject(error)
                        }
                    },

                    onShippingMethodSelected: async (resolve, reject, event) => {
                        try {
                            // Update the basket with shipping method returned from Apple Pay modal
                            await updateShippingMethodForShipment({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: basket.shipments[0].shipmentId
                                },
                                body: {
                                    id: event.shippingMethod.identifier
                                }
                            })
                            /// refetch the basket to get the updated shipping method and totals
                            const refetched = await refetchCurrentBasket()
                            const updatedBasket = refetched?.data?.baskets?.[0] || basket

                            // return success response to Adyen-Apple Pay
                            const update = {
                                newTotal: {
                                    label: applePayValidationData.merchantName,
                                    amount: getEstimatedBasketTotal(updatedBasket).toFixed(2),
                                    type: 'final'
                                }
                            }
                            resolve(update)
                        } catch (error) {
                            console.error('ApplePay onShippingMethodSelected error:', error)
                            reject(error)
                        }
                    },

                    onAuthorized: async (data, actions) => {
                        try {
                            const {billingAddress, deliveryAddress} = data
                            const shippingPhone = getApplePayContactField(data, 'phoneNumber')
                            const billingPhone = billingAddress?.phone || shippingPhone
                            const customerEmail = isRegistered ? customer?.email : getApplePayContactField(data, 'emailAddress')
                            if (customerEmail) {
                                await updateCustomerForBasket({
                                    parameters: {basketId: basket.basketId},
                                    body: {email: customerEmail}
                                })
                            }
                            await updateShippingAddressForShipment({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: basket.shipments[0].shipmentId
                                },
                                body: {
                                    firstName: deliveryAddress.firstName,
                                    lastName: deliveryAddress.lastName,
                                    address1: deliveryAddress.street,
                                    city: deliveryAddress.city,
                                    stateCode: deliveryAddress.stateOrProvince,
                                    postalCode: deliveryAddress.postalCode,
                                    countryCode: deliveryAddress.country,
                                    phone: shippingPhone
                                }
                            })

                            await updateBillingAddressForBasket({
                                parameters: {
                                    basketId: basket.basketId
                                },
                                body: {
                                    firstName: billingAddress.firstName || deliveryAddress.firstName,
                                    lastName: billingAddress.lastName || deliveryAddress.lastName,
                                    address1: billingAddress.street,
                                    city: billingAddress.city,
                                    stateCode: billingAddress.stateOrProvince,
                                    postalCode: billingAddress.postalCode,
                                    countryCode: billingAddress.country,
                                    phone: billingPhone
                                }
                            })
                            actions.resolve() // will call onSubmit
                        } catch (error) {
                            console.error('ApplePay onAuthorized error:', error)
                            actions.reject()
                        }
                    }
                })
                await applePayComponentRef.current.isAvailable()
                setIsApplePayAvailable(true)
                applePayComponentRef.current.mount(applePayContainerRef.current)
            } catch (error) {
                setIsApplePayAvailable(false)
            }
        }
        if (basket && applePayPaymentMethod && shippingMethodCount > 0) {
            initAdyenCheckoutAndLoadApplePayInContainer()
        }
        //unmount the previous ApplePay button if exists
        return () => {
            applePayComponentRef.current?.unmount()
            applePayComponentRef.current = null
        }
    }, [
        basketId,
        basketCurrency,
        basketAmountValue,
        localeId,
        siteId,
        shipmentId,
        shippingMethodCount,
        applePayPaymentMethodType,
        applePayMerchantName,
    ])

    return (
        <div ref={applePayContainerRef} style={{display: isApplePayAvailable ? 'block' : 'none'}}/>
    )
}

export default ApplePayExpressButton
